import * as React from 'react';
import CustomerIcon from 'lib/CustomerIcon';
import { Button, PageHeader } from '@salesforce/design-system-react';
import ViewButtons from './ViewButtons';
import { Constellation } from './Constellation';
import { BodyConfig, CardSpec } from 'lib/types';
import MuleSoft from './Mulesoft';

import './dash-card.css';
import { ScreenName } from 'lib/types';
import { withBrandData } from 'lib/withBrand';
import { NavFunctions } from 'lib/NavContext';

interface DashCardProps {
	brandData: CardSpec;
	navFunctions: NavFunctions;
	configured: boolean;
	screen: ScreenName;
}

interface DashCardState {
	modal: boolean;
}
class DashCard extends React.Component<DashCardProps, DashCardState> {
	animating: boolean;
	hoveredMoon: Node | null;
	tooltip: HTMLElement;
	svg: HTMLElement;
	systems: Constellation[];

	constructor (props: DashCardProps) {
		super(props);
		this.state = {
			modal: false
		};
	}

	componentDidMount () {
		const body = document.querySelector('body');
		if (body) { body.addEventListener('click', this.clickOutside); }
		this.renderUniverse();
	}

	componentDidUpdate () {
		this.renderUniverse();
	}

	componentWillReceiveProps(nextProps: DashCardProps) {
		if (nextProps.screen !== this.props.screen) {
			this.closeModal();
		}
	}

	componentWillUnmount () {
		const body = document.querySelector('body');
		if (body) { body.removeEventListener('click', this.clickOutside); }
	}

	clickOutside = () => {
		this.tooltip.style.display = 'none';
	}

	clickOtherData = (e: MouseEvent, config: BodyConfig) => {
		if (config.name === 'Other Data') {
			this.animating = true;
			setTimeout(() => this.animating = false, 500);
			this.systems[0].slideLeft();
			this.systems[1].slideCenter();
		}
	}

	closeModal = () => {
		this.setState({modal: false});
	}

	openModal = () => {
		this.setState({modal: true});
	}

	setupHome () {
		this.systems[0].onClick((e, config) => {
			if (config.name === 'Back >') {
				this.animating = true;
				setTimeout(() => this.animating = false, 500);
				this.systems[0].slideLeft();
				this.systems[1].slideCenter();
			}
		});
		this.systems[1].onClick((e, config) => {
			if (config.name === 'Salesforce Data') {
				this.animating = true;
				setTimeout(() => this.animating = false, 500);
				this.systems[1].slideRight();
				this.systems[0].slideCenter();
			}
		});
	}

	setupDashboard () {
		this.systems[0].onClick(this.clickOtherData);

		const muleSoft = this.systems[1];
		muleSoft.onClick((event, config) => { 
			switch (config.name) {
				case 'MuleSoft':
					this.openModal();
					break;
				case '< Back':
					this.animating = true;
					setTimeout(() => this.animating = false, 500);
					this.systems[1].slideRight();
					this.systems[0].slideCenter();
					break;
				default:
					return;
			}
		});

		// The Xtra Special Tooltip
		muleSoft.onMouseover((event, config) => {
			if (this.animating) { return; }
			const target = event.target as Element;

			if (event && config.name === 'MuleSoft' && target.classList.contains('svg-constellation_planet')) {
				this.hoveredMoon = target.parentNode;
				// tslint:disable-next-line:no-any
				const position = target.getBoundingClientRect() as any;
				const widthOffset = (window.innerWidth * 0.1875) - 60; // Adjusts tooltip hover based on screen width

				this.tooltip.style.display = 'block';
				this.tooltip.style.top = `${position.y - 240}px`;
				this.tooltip.style.left = `${position.x - widthOffset}px`;
			}
		});
		muleSoft.onMouseout((event, config) => {
			const target = event.target as Element;
			if (config.name === 'MuleSoft' && target.parentNode !== this.hoveredMoon) {
				this.clickOutside();
				this.tooltip.style.display = 'none';
			}
		});
	}

	setupMuleSoftConfigured () {
		this.systems[0].onClick(this.clickOtherData);

		const muleSoft = this.systems[1];
		muleSoft.onClick((event, config) => { 
			switch (config.name) {
				case 'Add More':
					this.openModal();
					break;
				case '< Back':
					this.animating = true;
					setTimeout(() => this.animating = false, 500);
					this.systems[1].slideRight();
					this.systems[0].slideCenter();
					break;
				default:
					return;
			}
		});

	}

	setupDashboard2 () {
		this.systems[0].onClick(this.clickOtherData);
		this.systems[1].onClick((e, config) => {
			if (config.name === '< Back') {
				this.animating = true;
				setTimeout(() => this.animating = false, 500);
				this.systems[1].slideRight();
				this.systems[0].slideCenter();
			}
		});
	}

	renderUniverse () {
		Array.from(this.svg.children).forEach((child) => child.remove());

		this.systems = this.props.brandData.constellations.map(
			(system, i) => new Constellation(this.svg, `system-${i}`, system.center, system.moons)
		);

		if (this.props.brandData.start === 0) {
			this.systems[1].startRight();
		} else {
			this.systems[0].startLeft();
		}

		this.systems.forEach((s) => s.render());

		if (this.props.brandData.nextClick) {
			const [systemId, moonId, nextScreen] = this.props.brandData.nextClick;
			const targetSystem = this.systems[systemId];
			const targetMoon = targetSystem.moonConfigs[moonId];
			targetSystem.onClick((e, config) => {
				if (config.name === targetMoon.name) {
					if (nextScreen) {
						this.props.navFunctions.goToAScreen(nextScreen);
					} else {
						this.props.navFunctions.goToNextScreen();
					}
				}
			});
		}

		switch (this.props.brandData.interaction) {
			case 'empty':
				return this.setupHome();
			case 'mulesoft':
				return this.setupDashboard();
			case 'mulesoft-configured':
				return this.setupMuleSoftConfigured();
			case 'other':
				return this.setupDashboard2();
			default:
				return;
		}
	}

	render () {
		return (
			<div>
				<PageHeader
					icon={<CustomerIcon />}
					navRight={<div><ViewButtons /></div>}
					label="Customer 360 Data Manager"
					title="Data Sources"
					variant="objectHome"
				/>
				<div
					style={{position: 'absolute', top: '65px', bottom: '0', width: '58.333%', overflow: 'hidden'}}
					ref={(svg) => this.svg = svg as HTMLElement}
				/>
				<div
					className="slds-popover slds-popover--tooltip slds-nubbin--left"
					id="mulesoft-source-info-tooltip"
					ref={(t) => this.tooltip = t as HTMLElement}
				>
					<div className="slds-popover__body">
						<h4 className="slds-text-heading_small">MuleSoft Anypoint Platform</h4>
						<p>
							You can now bring in the APIs that have been created in the 
							Anypoint Platform directly inside Salesforce Customer 360 to enrich 
							your customer information further. 
						</p>
						<img src="/assets/images/mulesoft-grid.png" />
						<Button 
							iconCategory="utility"
							iconName="new_window"
							iconPosition="left"
							label="Anypoint Platform" 
						/>
					</div>
				</div>
				<MuleSoft isOpen={this.state.modal} navClick={this.props.navFunctions.goToNextScreen} />
			</div>
		);
	}
}

export default withBrandData('card', DashCard);


// WEBPACK FOOTER //
// ./src/DataSources/DashCard.tsx